import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { PlusAuthenticationService } from '@karve.it/core';
import { MenuItem, SelectItem } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Observable, Subscription, filter, first, merge } from 'rxjs';
import { SubSink } from 'subsink';

import { ColorSchemeService } from '../color-scheme.service';
import { dayjs } from '../core/dayjs/dayjs';
import { AdvancedPeriodOptions, advancedPeriodOptions } from '../reports/reports.constants';
import { BrandingService } from '../services/branding.service';
import { DemoModeService } from '../services/demo-mode.service';
import { PermissionService } from '../services/permission.service';
import { QueryParamsService } from '../shared/query-params.service';
import { UUID_REGEX } from '../utilities/generic.util';

import { DASHBOARDS, DashboardEntry, getDashboardDisplay, getDashboardPermissions, isDashboardHidden, isDashboardVisibleInZone, isDashboardVisibleInZoneType } from './dashboards-list';
import { ComparedTo, DashboardStore, comparedToLsKey, comparedToValues, decodeUser, groupLeadsByValues, jobStages, pipelineMetrics, pipelinePeriods, salesLeaderboardRoles, salesLeaderboardSortKeys } from './dashboardv2.store';

type ValidationEntry<Validated> = {
  allowed: ((text: string) => Validated) | readonly Validated[];
  setValue: (value: Validated | Observable<Validated>) => Subscription;
};

// Regex to validate the format MMMM-YYYY (case-insensitive)
const MONTH_REGEX = /^(january|february|march|april|may|june|july|august|september|october|november|december)-\d{4}$/i;

@Component({
  selector: 'app-dashboardv2',
  templateUrl: './dashboardv2.component.html',
  styleUrls: [
    './dashboardv2.component.scss',
    '../shared/colors.scss',
  ],
  providers: [DashboardStore],
})
export class Dashboardv2Component implements OnInit, OnDestroy {

  visibleDashboards: DashboardEntry[] = [];

  dashboardSearch = '';

  filteredDashboards: DashboardEntry[] = [];

  @ViewChild('dashboardPanel') dashboardPanel: OverlayPanel;

  @ViewChild('dashboardSearchInput') dashboardSearchInput: ElementRef;

  home: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };

  breadcrumb: MenuItem[] = [
    { label: 'Reports', routerLink: '/reports' },
    { label: 'Dashboards', routerLink: '/reports/dashboards' },
  ];

  dashboardTitle = '';

  dashboardIcon = '';

  dashboardDisplay = getDashboardDisplay(undefined);

  periodOptions = [
    'Month of Year',
    ...advancedPeriodOptions,
  ];

  currencyOptions = [
    'USD',
    'CAD',
    'All',
  ] as const;

  comparedToOptions: SelectItem<ComparedTo>[] = comparedToValues
    .map((val) => ({
      label: `Compared to ${val}`,
      value: val,
    }));


  month: string;

  customPeriod: Date[];

  lastValidCustomPeriod: Date[];

  setPeriod = this.dashboardStore.setPeriod;

  setMonth = this.dashboardStore.setMonth;

  setComparedTo = this.dashboardStore.setComparedTo;

  setCurrency = this.dashboardStore.setCurrency;

  settings$ = this.dashboardStore.settingsViewModel$;

  subs = new SubSink();

  user = this.plusAuth.user;

  constructor(
    private dashboardStore: DashboardStore,
    private route: ActivatedRoute,
    public router: Router,
    private queryParamsService: QueryParamsService,
    public colorScheme: ColorSchemeService,
    private plusAuth: PlusAuthenticationService,
    public demoModeService: DemoModeService,
    private branding: BrandingService,
    private permissionService: PermissionService,
  ){}

  ngOnInit(): void {
    this.watchSettings();
    this.readUrlParams();
    this.watchActiveDashboard();
    // Requirement: recompute on zone change AND on permission change. The
    // branding query delivers the new zone's permissions *after* currentZone
    // emits, so a zone-only subscription builds the list from the previous
    // zone's permissions and never revisits it. Both sources replay their
    // current value on subscribe, so this also covers the initial render.
    this.subs.sink = merge(
      this.branding.currentZone(),
      this.permissionService.watch(),
    ).subscribe(() => {
      this.recomputeDashboards();
      this.updateBreadcrumb();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private currentSlug = '';

  private watchActiveDashboard(): void {
    // Read the slug immediately (initial navigation already completed before ngOnInit).
    this.readSlugFromRoute();

    // Listen for subsequent navigations between dashboards.
    this.subs.sink = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    ).subscribe(() => {
      this.readSlugFromRoute();
    });
  }

  private readSlugFromRoute(): void {
    // The Dashboardv2Component route has children with dashboardSlug in their data.
    // this.route.firstChild is the active child route.
    this.currentSlug = this.route.firstChild?.snapshot?.data?.['dashboardSlug'] ?? '';
    this.updateBreadcrumb();
  }

  private updateBreadcrumb(): void {
    // Search the full DASHBOARDS list (not just visibleDashboards) so that
    // hidden dashboards still show their title when accessed by direct link.
    const entry = DASHBOARDS.find((t) => t.slug === this.currentSlug);
    this.dashboardTitle = entry?.name ?? '';
    this.dashboardIcon = entry?.icon ?? '';
    this.dashboardDisplay = getDashboardDisplay(entry);
    this.breadcrumb = [
      { label: 'Reports', routerLink: '/reports' },
      { label: 'Dashboards', routerLink: '/reports/dashboards' },
      ...(this.dashboardTitle ? [{
        label: this.dashboardTitle,
        icon: this.dashboardIcon,
        styleClass: 'dashboard-breadcrumb-link',
        command: (e: { originalEvent: Event }) => this.showDashboardPicker(e.originalEvent),
      }] : []),
    ];
  }

  showDashboardPicker(event?: Event): void {
    this.dashboardSearch = '';
    this.filteredDashboards = [...this.visibleDashboards];
    const target = (event?.target as HTMLElement) ?? null;
    this.dashboardPanel?.toggle(event, target);
    setTimeout(() => this.dashboardSearchInput?.nativeElement?.focus(), 50);
  }

  filterDashboards(): void {
    const q = this.dashboardSearch.toLowerCase().trim();
    this.filteredDashboards = q
      ? this.visibleDashboards.filter((d) => d.name.toLowerCase().includes(q))
      : [...this.visibleDashboards];
  }

  private recomputeDashboards(): void {
    const zone = this.branding.currentZone().value;
    const zoneId = zone?.id;
    const zoneType = zone?.type;
    this.visibleDashboards = DASHBOARDS.filter((d) => {
      if (isDashboardHidden(d)) { return false; }
      const perms = getDashboardPermissions(d);
      if (perms.length && !perms.every((p) => this.permissionService.checkPermission(p))) { return false; }
      if (!isDashboardVisibleInZone(d, zoneId)) { return false; }
      if (!isDashboardVisibleInZoneType(d, zoneType)) { return false; }
      return true;
    });
    this.filteredDashboards = [...this.visibleDashboards];
    this.updateBreadcrumb();
  }

  onDashboardSelect(slug: string): void {
    if (slug) {
      this.router.navigate(['/reports', 'dashboards', slug]);
    }
  }

  watchSettings() {
    this.subs.sink = this.settings$.subscribe((settings) => {
      this.month = settings.month;
      // Convert string[] from store to Date[] for calendar display
      this.customPeriod = settings.customPeriod.map((dateStr) => dayjs(dateStr).toDate());
      this.lastValidCustomPeriod = this.customPeriod;
    });
  }

  readUrlParams() {
    this.subs.sink = this.route.queryParams
      .pipe(
        filter(params => 'zone' in params),
        first(),
      )
      .subscribe((params) => {

        const copy = { ...params };

        const validPeriod = this.isValidPeriod(params.period);

        type DashboardQueryParamKey =
          | 'currency'
          | 'customPeriod'
          | 'month'
          | 'comparedTo'
          | 'pipelinePeriod'
          | 'metric'
          | 'groupLeadsBy'
          | 'jobStage'
          | 'status'
          | 'estimator'
          | 'salesAgent'
          | 'role'
          | 'salesLeaderboardSortKey'
          | 'salesLeaderboardSortDesc';

        const validationMap: Partial<Record<DashboardQueryParamKey, ValidationEntry<any>>> = {
          currency: {
            allowed: this.currencyOptions,
            setValue: this.setCurrency,
          },
          customPeriod: {
            allowed: (text: string) => this.queryParamsService.parseStartEndQueryParamAsStrings(text),
            setValue: this.dashboardStore.setCustomPeriod,
          },
          month: {
            allowed: this.isValidMonth,
            setValue: this.setMonth,
          },
          comparedTo: {
            setValue: this.setComparedTo,
            allowed: this.isValidComparedTo,
          },
          groupLeadsBy: {
            setValue: this.dashboardStore.setGroupLeadsBy,
            allowed: groupLeadsByValues,
          },
          metric: {
            setValue: this.dashboardStore.setPipelineMetric,
            allowed: pipelineMetrics,
          },
          pipelinePeriod: {
            setValue: this.dashboardStore.setPipelinePeriod,
            allowed: pipelinePeriods,
          },
          jobStage: {
            setValue: this.dashboardStore.setJobStage,
            allowed: jobStages,
          },
          status: {
            setValue: this.dashboardStore.setStatus,
            allowed: [ 'open', 'closed' ] as const,
          },
          estimator: {
            setValue: this.dashboardStore.setEstimator,
            allowed: this.isValidUser,
          },
          salesAgent: {
            setValue: this.dashboardStore.setSalesAgent,
            allowed: this.isValidUser,
          },
          role: {
            setValue: this.dashboardStore.setRole,
            allowed: salesLeaderboardRoles,
          },
          salesLeaderboardSortKey: {
            setValue: this.dashboardStore.setSalesLeaderboardSortKey,
            allowed: salesLeaderboardSortKeys,
          },
          salesLeaderboardSortDesc: {
            setValue: (val: string) => this.dashboardStore.setSalesLeaderboardSortDesc(val === 'true'),
            allowed: (text: string) => text === 'true' || text === 'false' ? text : null,
          },
        };

        if (!validPeriod) {
          this.clearParams();
          this.checkLocalStorage();
          return;
        }

        this.setPeriod(validPeriod);

        let hasInvalidParams = false;

        const validate = (key: DashboardQueryParamKey) => {

          if (!validationMap[key]) {
            throw new Error(`Invalid key ${key} for validation map`);
          }

          const isValid = this.checkParam({
            copy,
            params,
            key,
            validation: validationMap[key],
          });

          if (!isValid) {
            hasInvalidParams = true;
          }
        }

        validate('currency');
        validate('groupLeadsBy');
        validate('metric');
        validate('pipelinePeriod');
        validate('jobStage');
        validate('status');
        validate('estimator');
        validate('salesAgent');
        validate('role');
        validate('salesLeaderboardSortKey');
        validate('salesLeaderboardSortDesc');

        if (validPeriod === 'Custom') {
          validate('customPeriod');
        } else if (params.customPeriod) {
          copy.customPeriod = null;
          hasInvalidParams = true;
        }

        if (validPeriod === 'Month of Year') {
          validate('month');
        } else if (params.month) {
          copy.month = null;
          hasInvalidParams = true;
        }

        if (validPeriod === 'Month of Year' || validPeriod === 'MTD') {
          validate('comparedTo');
        } else if (params.comparedTo) {
          copy.comparedTo = null;
          hasInvalidParams = true;
        }

        if ((validPeriod === 'Month of Year' || validPeriod === 'MTD') && copy.comparedTo === null) {
          this.checkLocalStorage();
        }

        if (typeof copy.source === 'string') {
          this.dashboardStore.setSource(copy.source.replaceAll('-', ' '));
        }

        if (typeof copy.jobOrigin === 'string') {
          this.dashboardStore.setJobOrigin(copy.jobOrigin.replaceAll('-', ' '));
        }

        if (typeof copy.estimateMethod === 'string') {
          this.dashboardStore.setEstimateMethod(copy.estimateMethod.replaceAll('-', ' '));
        }

        if (typeof copy.attentionExplanation === 'string') {
          this.dashboardStore.setAttentionExplanation(copy.attentionExplanation);
        }

        if (typeof copy.jobClosureReason === 'string') {
          this.dashboardStore.setJobClosureReason(copy.jobClosureReason);
        }

        if (typeof copy.utmSource === 'string') {
          this.dashboardStore.setUtmSource(copy.utmSource.replaceAll('-', ' '));
        }

        if (typeof copy.utmMedium === 'string') {
          this.dashboardStore.setUtmMedium(copy.utmMedium.replaceAll('-', ' '));
        }

        if (typeof copy.utmCampaign === 'string') {
          this.dashboardStore.setUtmCampaign(copy.utmCampaign.replaceAll('-', ' '));
        }

        if (typeof copy.utmContent === 'string') {
          this.dashboardStore.setUtmContent(copy.utmContent.replaceAll('-', ' '));
        }

        if (typeof copy.utmTerm === 'string') {
          this.dashboardStore.setUtmTerm(copy.utmTerm.replaceAll('-', ' '));
        }

        if (hasInvalidParams) {
          this.router.navigate([], {
            queryParams: copy,
            queryParamsHandling: 'merge',
          });
        }

        // function validate(key: DashboardQueryParamKey) {

      });
  }

  isValidPeriod(text: string) {
    if (!text) { return };
    const decoded = text.replaceAll('-', ' ');
    for (const val of [ ...advancedPeriodOptions, 'Month of Year' ]) {
      if (val === decoded) {
        return decoded as (AdvancedPeriodOptions | 'Month of Year');
      }
    }
  }

  isValidMonth(text: string): string | null {
    if (!text || typeof text !== 'string') return null;

    // Validate the format
    if (!MONTH_REGEX.test(text)) return null;

    // Replace hyphens with spaces and trim extra whitespace
    const normalizedText = text.replace(/-/g, ' ').trim();
    const parsedDate = dayjs(normalizedText, 'MMMM YYYY', true);

    return parsedDate.isValid() ? parsedDate.format('MMMM YYYY') : null;
  }

  isValidComparedTo(text: string) {
    if (!text) { return };
    const decoded = text.replaceAll('-', ' ');
    for (const val of comparedToValues) {
      if (val === decoded) {
        return decoded;
      }
    }
  }

  isValidUser(text: string) {
    if (!text) { return };

    if (text === 'None') {
      return text;
    }

    if (text.split('.').length !== 3) { return; }

    const user = decodeUser(text);

    if (!UUID_REGEX.test(user.id)) { return; }

    return text;
  }

  checkLocalStorage() {
    const validComparedToFromLocalStorage = this.isValidComparedTo(localStorage.getItem(comparedToLsKey));

    if (validComparedToFromLocalStorage) {
      this.setComparedTo(validComparedToFromLocalStorage);
    }
  }

  clearParams() {
    return this.router.navigate([], {
      queryParams: {
        period: null,
        customPeriod: null,
        month: null,
        comparedTo: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  checkParam<K>(input: {
    params: Params;
    key: string;
    copy: { [x: string]: any };
    validation: ValidationEntry<K>;
  }): boolean {

    const {
      params,
      key,
      copy,
      validation: {
        allowed,
        setValue,
      }
    } = input;

    let getValidValue: (text: string) => K;

    if (!isArray(allowed)) {
      getValidValue = allowed;
    } else {

      getValidValue = (getValidValueInput: string) => {

        let value = getValidValueInput;

        if (typeof value === 'string') {
          value = value.replaceAll('-', ' ');
        }

        if (!isInArray(allowed, value)) { return; }

        return value;
      }
    }

    const validValue = getValidValue(params[key]);

    if (validValue) {
      setValue(validValue);
      return true;
    }

    copy[key] = null;
    return false;
  }

  validateCustomPeriod() {
    if (this.customPeriod.filter(Boolean).length === 2) {
      // Convert Date[] to string[] for the store
      const customPeriodStrings = this.customPeriod.map((date) => dayjs(date).format('YYYY-MM-DD'));
      this.dashboardStore.setCustomPeriod(customPeriodStrings);
    } else {
      this.customPeriod = this.lastValidCustomPeriod;
    }
  }
}

function isArray<T>(value: readonly T[] | ((text: string) => T | undefined)): value is readonly T[] {
  return Array.isArray(value);
}

function isInArray<T>(array: readonly T[], value: unknown): value is T {
  return array.includes(value as T);
}
